import { ReactNode, useEffect, useRef, useState } from 'react'
import { ReservedBlock } from './styles'

/**
 * Altura a reservar enquanto o bloco nao montou, por faixa de largura.
 *
 * A vitrine muda de tamanho no breakpoint md do Chakra (48em / 768px): medida
 * em 10 larguras, ela fica constante de cada lado e salta entre 600 e 768px.
 * Por isso dois valores e nao um — reservar so o maior sobraria ~230px no
 * celular, e o bloco encolheria ao montar.
 */
export type ReservedHeight = {
  /** Ate 48em, exclusive. */
  belowMd: string
  /** A partir de 48em. */
  fromMd: string
}

/**
 * ProductSection, usado na PDP, na home e nas paginas de erro.
 * Maior altura medida por faixa: 527px ate md (o pico esta em 360px de tela;
 * entre 414 e 600px mede 499 a 523px) e 743px a partir de md, constante de
 * 768px a 1600px.
 */
export const PRODUCT_SHOWCASE_HEIGHT: ReservedHeight = {
  belowMd: '32.9375rem',
  fromMd: '46.4375rem',
}

/**
 * ProductSectionSearch, usado na busca e na categoria. E outro componente e
 * mede menos que o ProductSection — reservar a altura da PDP encolheria o
 * bloco ao montar.
 *
 * Maior altura medida por faixa: 628px ate md (o pico esta em 600px de tela,
 * nao em 360px — a 360px mede 592px) e 679px a partir de md.
 */
export const SEARCH_SHOWCASE_HEIGHT: ReservedHeight = {
  belowMd: '39.25rem',
  fromMd: '42.4375rem',
}

/**
 * Quanto antes da viewport a montagem comeca. Meia tela de folga da tempo de
 * o bloco existir antes de o usuario chegar nele — sem isso, rolar rapido
 * mostraria espaco vazio.
 */
export const APPROACH_MARGIN = '600px 0px'

type DeferredMountProps = {
  children: ReactNode
  /** Altura a reservar enquanto o bloco nao montou. */
  reservedHeight?: ReservedHeight
}

/**
 * Adia a montagem de uma subarvore ate ela se aproximar da viewport.
 *
 * Medido na pagina de produto: as vitrines sao 73% dos nos de DOM (7584 caem
 * para 2034 sem elas) e 50% do TBT (16920 para 8459 ms). Cada bloco tem ~1800
 * nos e fica muito abaixo da dobra, mas era montado inteiro assim que a API de
 * vitrines respondia — por isso 91% do TBT acontecia depois de 6 segundos, com
 * uma unica trava de 3414 ms.
 *
 * Tres decisoes que o teste trava:
 *
 * - A altura fica reservada ate montar. Trocar custo de CPU por deslocamento
 *   de layout seria piorar o conjunto, e o Req 07 proibe exatamente isso.
 * - Uma vez montado, nao desmonta. Desmontar ao sair da viewport devolveria o
 *   custo a cada rolagem e perderia o estado do carrossel.
 * - Sem IntersectionObserver, monta na hora. Adiar seria esconder produto.
 *
 * Nao afeta SEO: as vitrines ja eram client-only — o hook as busca em efeito,
 * e o HTML servido nunca as continha.
 */
export const DeferredMount = ({
  children,
  reservedHeight = PRODUCT_SHOWCASE_HEIGHT,
}: DeferredMountProps) => {
  const ancora = useRef<HTMLDivElement>(null)
  /**
   * Comeca sempre false, inclusive sem IntersectionObserver. Decidir aqui pelo
   * ambiente daria arvores diferentes no servidor e no cliente — o servidor
   * renderizaria os filhos, o cliente o espaco reservado — e a hidratacao
   * quebraria. Quem nao tem a API monta no efeito abaixo, que so roda no
   * cliente, entao o resultado final e o mesmo sem o risco.
   */
  const [montado, setMontado] = useState(false)

  useEffect(() => {
    if (montado) return

    if (typeof IntersectionObserver === 'undefined') {
      setMontado(true)
      return
    }

    const observador = new IntersectionObserver(
      (entradas) => {
        if (!entradas.some((entrada) => entrada.isIntersecting)) return
        setMontado(true)
        observador.disconnect()
      },
      { rootMargin: APPROACH_MARGIN }
    )

    if (ancora.current) observador.observe(ancora.current)

    return () => observador.disconnect()
  }, [montado])

  /**
   * A altura reservada permanece DEPOIS de montar, de proposito.
   *
   * Remove-la no instante da montagem colapsava o bloco: naquele quadro os
   * filhos ainda nao tem altura — imagens sem carregar, carrossel sem
   * inicializar — e a caixa caia de 743px para quase nada, levando a pagina
   * inteira junto. Medido sob rolagem rapida: CLS de 0,111 para 1,342, com as
   * maiores fontes sendo o container e o rodape, nao a vitrine.
   *
   * Como min-height e um MINIMO, conteudo mais alto continua mandando; o
   * unico custo e um resto de espaco quando a vitrine mede menos que o
   * reservado, e os valores acima sao o maior medido em cada faixa.
   */
  return (
    <ReservedBlock
      ref={ancora}
      heightBelowMd={reservedHeight.belowMd}
      heightFromMd={reservedHeight.fromMd}
    >
      {montado ? children : null}
    </ReservedBlock>
  )
}
